import { ref, computed, ComputedRef, inject, onBeforeUnmount } from "vue";
import { debounce } from "lodash";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import useWindowDimensions from "@/util/useWindowDimensions.ts";
import { useApiStore } from "@/stores/api/index.ts";
import * as autoOpenSearch from "@/components/Global/providedData/autoOpenSearch.ts";
import { PartialCategory } from "@/types/category.ts";
import {
  SEARCH_DEBOUNCE,
  SEARCH_DEBOUNCE_TOUCHSCREEN,
} from "@/util/constants.ts";
import { useCategoriesStore } from "@/stores/categories/index.ts";
import { useSearchStore } from "@/stores/search/index.ts";

interface MediaQueryResult {
  mediaID: string;
  position?: number;
  score: string;
}

export interface SearchOptionV2 {
  isLive: boolean;
  name: string;
  parentID?: string;
  parentName?: string;
  path: string;
  thumbnail?: string;
  type: "category" | "subcategory" | "entry";
  mediaQueryResults?: MediaQueryResult[];
}

export default function useSearchV2() {
  const store = useStore();
  const categoriesStore = useCategoriesStore();
  const api = useApiStore();
  const router = useRouter();

  const { isMobileViewport } = useWindowDimensions();

  const { addVisitedItem, getVisitedItems, clearVisitedItems } =
    useSearchStore();

  const { disableAutoOpenSearch } = inject(
    autoOpenSearch.key,
    autoOpenSearch.defaultApi
  );

  const isSearchLoading = ref(false);
  const showSearchButton = ref(navigator.onLine);

  const searchQuery = computed({
    get: () => store.getters.getSearchQuery,
    set: (value) => store.dispatch("setSearchQuery", value),
  });

  const searchOptions = computed(() => store.getters.getSearchResults);

  // @todo: We need to add a backend endpoint to populate this,
  // but in the meantime, we'll just use the top level categories.
  const popularSearchOptions: ComputedRef<SearchOptionV2[]> = computed(
    () =>
      categoriesStore
        .get()
        ?.categories?.filter((category: PartialCategory) => category.isLive)
        .map((item: PartialCategory) => ({
          name: item.categoryName ?? "",
          type: item.hasSubCategories ? "subcategory" : "category",
          path: item.path,
          isLive: item.isLive,
        })) ?? []
  );

  const showCMS = computed(() => store.getters.showCMS);

  function transformOptions(
    options: SearchOptionV2[],
    query: string
  ): SearchOptionV2[] {
    const expandedOptions: SearchOptionV2[] = options.flatMap((option) => {
      if (
        option.mediaQueryResults &&
        Array.isArray(option.mediaQueryResults) &&
        option.mediaQueryResults.length > 0
      ) {
        return option.mediaQueryResults.map((media) => {
          let label = "";
          if (media.position) {
            label = ` (Page ${media.position + 1})`; // +1 because the position is 0-indexed
          }
          return {
            ...option,
            mediaQueryResults: [media],
            name: option.name + label,
          };
        });
      }
      return [option];
    });

    function nonCmsFilters(option: SearchOptionV2) {
      if (!option.isLive) {
        return false;
      }

      if (
        ["NEW PROFILE", "NEW FOLDER"].some((name) =>
          name.startsWith(option.name)
        )
      ) {
        return false;
      }

      return true;
    }
    return expandedOptions
      .sort((a) =>
        a.name.toLowerCase().startsWith(query.toLowerCase()) ? -1 : 1
      )
      .filter((option) =>
        showCMS.value ? true /* show everything */ : nonCmsFilters(option)
      );
  }

  async function fetchOptions(query: string): Promise<void> {
    try {
      await store.dispatch(
        "setSearchResults",
        transformOptions(
          (
            await api.fetchSearchOptionsV2(
              query,
              !isMobileViewport.value // don't hydrate media on mobile
            )
          ).data,
          query
        )
      );
    } catch (err) {
      console.error("Error fetching search options", err);
      await store.dispatch("setSearchResults", []);
    }

    isSearchLoading.value = false;
  }

  const onSearchQueryChangedDebounced = debounce(
    async (value: string) => {
      await store.dispatch("setSearchResults", []);

      if (!value || value.length < 3) {
        return;
      }

      fetchOptions(value);
    },
    store.getters.getTouchScreen ? SEARCH_DEBOUNCE_TOUCHSCREEN : SEARCH_DEBOUNCE
  );

  function onSearchQueryChanged(value: string) {
    isSearchLoading.value = true;
    onSearchQueryChangedDebounced(value);
  }

  function getRouteName(option: SearchOptionV2) {
    switch (option.type) {
      case "category":
        return "Category";
      case "subcategory":
        return "SubCategories";
      case "entry":
        return "Person";
      default:
        return "Home";
    }
  }

  function getRouteParams(option: SearchOptionV2) {
    if (option.type === "entry") {
      return {
        categoryID: option.parentID,
        entryID: option.path.split("/")[2],
      };
    }
    return {
      categoryID: option.path.split("/")[2],
    };
  }

  function selectOption(option: SearchOptionV2) {
    disableAutoOpenSearch();
    addVisitedItem(`${option.path}${option.name}`);

    router.push({
      name: getRouteName(option),
      params: {
        ...getRouteParams(option),
        styleid: store.getters.getStyleID,
      },
      query: {
        fromSearch: 1,
        mediaID: option?.mediaQueryResults?.[0]?.mediaID,
      },
    });
  }

  function onSearchSelected(searchOption: SearchOptionV2) {
    selectOption(searchOption);
  }

  function handleOnline() {
    showSearchButton.value = true;
  }

  function handleOffline() {
    showSearchButton.value = false;
  }

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  onBeforeUnmount(() => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
  });

  return {
    popularSearchOptions,
    searchOptions,
    searchQuery,
    onSearchQueryChanged,
    onSearchSelected,
    isSearchLoading,
    showSearchButton,
    getVisitedItems,
    clearVisitedItems,
  };
}
